import { Hash, Loader2, MoreHorizontal, Pencil, Plus, Search, Tag as TagIcon, Trash2, Users, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { PageHeader, useConfirm, useToast } from "@/components/shared";
import { tagService, teamService } from "@/lib/services";
import type { TagScope, TagSummary, Team } from "@/types";

const TAG_COLORS = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#a855f7", // purple
  "#ec4899", // pink
  "#f43f5e", // rose
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#eab308", // yellow
  "#84cc16", // lime
  "#22c55e", // green
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#6b7280", // gray
];

export function Tags() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [tags, setTags] = useState<TagSummary[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState<"all" | TagScope>("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTag, setEditingTag] = useState<TagSummary | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!selectedTag) return;
    const handleClick = () => setSelectedTag(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [selectedTag]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    color: "#6366f1",
    scope: "personal" as TagScope,
    teamUuid: "",
  });

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tagService.list({
        pageSize: 100,
        scope: scopeFilter === "all" ? undefined : scopeFilter,
      });
      setTags(response.data);
    } catch (err) {
      console.error("Failed to load tags:", err);
      showToast("Failed to load tags", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, scopeFilter]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await teamService.list();
      setTeams(response.data);
    } catch {
      // Not critical — team picker will just be empty
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleDeleteTag = async (tagUuid: string) => {
    setSelectedTag(null);
    const confirmed = await confirm({
      title: "Delete Tag",
      message:
        "Are you sure you want to delete this tag? It will be removed from all entities. This action cannot be undone.",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await tagService.delete(tagUuid);
      showToast("Tag deleted successfully", "success");
      await fetchTags();
    } catch (err) {
      console.error("Failed to delete tag:", err);
      showToast("Failed to delete tag", "error");
    }
  };

  const filteredTags = tags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tag.teamName?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const openCreateModal = () => {
    setEditingTag(null);
    setFormData({ name: "", color: "#6366f1", scope: "personal", teamUuid: "" });
    setShowCreateModal(true);
  };

  const openEditModal = (tag: TagSummary) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color,
      scope: tag.scope,
      teamUuid: tag.teamUuid || "",
    });
    setShowCreateModal(true);
    setSelectedTag(null);
  };

  const handleSaveTag = async () => {
    if (!formData.name.trim()) {
      showToast("Please enter a tag name", "warning");
      return;
    }
    if (formData.scope === "team" && !formData.teamUuid) {
      showToast("Please select a team for this tag", "warning");
      return;
    }

    try {
      setSaving(true);
      if (editingTag) {
        await tagService.update(editingTag.uuid, {
          name: formData.name.trim(),
          color: formData.color,
        });
        showToast("Tag updated successfully", "success");
      } else {
        await tagService.create({
          name: formData.name.trim(),
          color: formData.color,
          scope: formData.scope,
          teamUuid: formData.scope === "team" ? formData.teamUuid : undefined,
        });
        showToast("Tag created successfully", "success");
      }
      setShowCreateModal(false);
      setEditingTag(null);
      setFormData({ name: "", color: "#6366f1", scope: "personal", teamUuid: "" });
      await fetchTags();
    } catch (err) {
      console.error("Failed to save tag:", err);
      showToast(editingTag ? "Failed to update tag" : "Failed to create tag", "error");
    } finally {
      setSaving(false);
    }
  };

  const getScopeBadge = (scope: TagScope, teamName?: string) => {
    if (scope === "team") {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 flex items-center gap-1">
          <Users className="h-3 w-3" />
          {teamName || "Team"}
        </span>
      );
    }
    return <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400">Personal</span>;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader
        icon={TagIcon}
        title="Tags"
        subtitle="Organize and categorize your resources with tags"
        actions={
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            New Tag
          </button>
        }
      >
        {/* Search & Filters */}
        <div className="mt-4 flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
            <input
              type="text"
              placeholder="Search tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
          <div className="flex items-center gap-1 bg-surface-secondary rounded-lg p-1">
            {(["all", "personal", "team"] as const).map((scope) => (
              <button
                key={scope}
                onClick={() => setScopeFilter(scope)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors capitalize ${
                  scopeFilter === scope
                    ? "bg-surface-primary text-content-primary shadow-xs"
                    : "text-content-tertiary hover:text-content-secondary"
                }`}
              >
                {scope}
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {filteredTags.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredTags.map((tag) => (
              <div
                key={tag.uuid}
                className="relative bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${tag.color}20` }}
                    >
                      <TagIcon className="h-5 w-5" style={{ color: tag.color }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-content-primary truncate">{tag.name}</h3>
                      <div className="flex items-center gap-2 mt-1">{getScopeBadge(tag.scope, tag.teamName)}</div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTag(selectedTag === tag.uuid ? null : tag.uuid);
                    }}
                    className="p-1.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                  {/* Actions Dropdown */}
                  {selectedTag === tag.uuid && (
                    <div
                      className="absolute right-2 top-10 z-50 bg-surface-primary border border-border-primary rounded-lg shadow-xl py-1 min-w-[160px]"
                      role="presentation"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          openEditModal(tag);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content-secondary hover:bg-interactive-hover transition-colors"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit Tag
                      </button>
                      <div className="border-t border-border-primary my-1" />
                      <button
                        onClick={() => handleDeleteTag(selectedTag)}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-interactive-hover transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete Tag
                      </button>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm text-content-tertiary">
                    <Hash className="h-3.5 w-3.5" />
                    <span>
                      {tag.entityCount} {tag.entityCount === 1 ? "entity" : "entities"}
                    </span>
                  </div>
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: tag.color }} title={tag.color} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center mb-4">
              <TagIcon className="h-8 w-8 text-brand-primary" />
            </div>
            <h3 className="text-lg font-medium text-content-primary">No tags yet</h3>
            <p className="text-content-tertiary mt-1 text-center max-w-sm">
              {searchQuery
                ? "Try adjusting your search query"
                : "Create tags to organize and categorize your agents, chats, data sources, and more."}
            </p>
            {!searchQuery && (
              <button
                onClick={openCreateModal}
                className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                <Plus className="h-4 w-4" />
                New Tag
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <h2 className="text-lg font-semibold text-content-primary">{editingTag ? "Edit Tag" : "New Tag"}</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingTag(null);
                  setFormData({ name: "", color: "#6366f1", scope: "personal", teamUuid: "" });
                }}
                disabled={saving}
                className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label htmlFor="tag-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Tag Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="tag-name"
                  type="text"
                  placeholder="e.g., High Priority"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                />
              </div>

              {/* Color Picker */}
              <div>
                <span className="block text-sm font-medium text-content-secondary mb-1.5">Color</span>
                <div className="flex flex-wrap gap-2">
                  {TAG_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setFormData({ ...formData, color })}
                      className={`h-8 w-8 rounded-lg transition-all ${
                        formData.color === color
                          ? "ring-2 ring-offset-2 ring-offset-surface-primary ring-brand-primary scale-110"
                          : "hover:scale-105"
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Scope (only when creating) */}
              {!editingTag && (
                <div>
                  <span className="block text-sm font-medium text-content-secondary mb-1.5">Scope</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setFormData({ ...formData, scope: "personal", teamUuid: "" })}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        formData.scope === "personal"
                          ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                          : "border-border-secondary text-content-tertiary hover:text-content-secondary hover:border-border-primary"
                      }`}
                    >
                      Personal
                    </button>
                    <button
                      onClick={() => setFormData({ ...formData, scope: "team" })}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        formData.scope === "team"
                          ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                          : "border-border-secondary text-content-tertiary hover:text-content-secondary hover:border-border-primary"
                      }`}
                    >
                      Team
                    </button>
                  </div>
                </div>
              )}

              {/* Team Selector (when scope is team and creating) */}
              {!editingTag && formData.scope === "team" && (
                <div>
                  <label htmlFor="tag-team" className="block text-sm font-medium text-content-secondary mb-1.5">
                    Team <span className="text-red-400">*</span>
                  </label>
                  {teams.length > 0 ? (
                    <select
                      id="tag-team"
                      value={formData.teamUuid}
                      onChange={(e) => setFormData({ ...formData, teamUuid: e.target.value })}
                      className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                    >
                      <option value="">Select a team...</option>
                      {teams.map((team) => (
                        <option key={team.uuid} value={team.uuid}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-content-tertiary">
                      You need to be a member of a team to create team tags.
                    </p>
                  )}
                </div>
              )}

              {/* Preview */}
              <div>
                <span className="block text-sm font-medium text-content-secondary mb-1.5">Preview</span>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: `${formData.color}20`,
                      color: formData.color,
                    }}
                  >
                    <TagIcon className="h-3.5 w-3.5" />
                    {formData.name || "Tag name"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingTag(null);
                  setFormData({ name: "", color: "#6366f1", scope: "personal", teamUuid: "" });
                }}
                disabled={saving}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTag}
                disabled={saving || !formData.name.trim()}
                className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(() => {
                  if (saving) {
                    return (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                        {editingTag ? "Saving..." : "Creating..."}
                      </>
                    );
                  }
                  return editingTag ? "Save Changes" : "Create Tag";
                })()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
